import * as React from 'react';
import gql from 'graphql-tag';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import {Component, deps, inject, observer} from '../../lib/component';
import {classes, style} from 'typestyle';
import {important, percent, px, rem} from 'csx';
import {
  centerCenter,
  end,
  endJustified,
  horizontal,
  horizontallySpaced,
  start,
  startJustified,
  vertical,
  verticallySpaced,
  wrap
} from 'csstips';
import maybe from '../../lib/maybe';
import {SimilarTrack, Track, YoutubeContentOwner} from '../../types/graphql';
import {shadowFirm} from '../../theme/shadow';
import {colorSubtle, colorWhite} from '../../theme/color';
import {mediaMobileOnly} from '../../theme/media';
import {ButtonPill} from '../button-pill';
import {ReleaseTracks, ReleaseTracksModel} from '../release-tracks';
import {BrandSponsoredLabelCompat} from '../compat/brand-sponsored-label-compat';
import {PlayButtonCompat} from '../compat/play-button-compat';
import {FavouriteButtonCompat} from '../compat/favourite-button-compat';
import {AddRemoveCompat} from '../compat/add-remove-compat';
import {Loading} from '../loading/loading';
import {ReleaseTile} from '../release-tile';
import {constantGoldenRatio} from '../../theme/constant';
import {Alert} from '../alert';
import {formatNameList} from '../../lib/helpers';

type Props = {
  identity: string;
  i18n?: {
    warning(contentOwners: YoutubeContentOwner[]): string;
  }
}

type Data = {
  getTrack: Track
}

type CombinedProps = Props & InjectedGraphQLProps<Data>;

@inject(deps) @observer
class TrackVouchStatus extends Component<CombinedProps> {
  render() {
    const {data, i18n = TrackVouchStatus.i18n} = this.props;

    const status = maybe(
      () => data.getTrack!.vouch_status,
      null
    );

    const contentOwners = maybe(
      () => data.getTrack!.youtube_content_owners.filter(o => !o.active),
      null
    );

    if (!contentOwners || !status || status !== 'OVERRIDDEN')
      return null;

    return (
      <Alert type="simple" position={'top-left'}>
        {i18n.warning(contentOwners)}
      </Alert>
    )
  }

  static i18n = {
    warning: (contentOwners: YoutubeContentOwner[]) => ([
      `Just so you know this track isn't completely claims free!`,
      `We work closely with`,
      formatNameList(contentOwners),
      `to remove copyright claims as quickly as possible through our automated systems.`,
      `Typically the claim will be removed within 15 minutes.`,
      `Thanks for understanding!`
    ].join(' '))
  };

  static styles = {
    container: style({
      zIndex: 1,
      background: colorSubtle.fade(0.075).toString(),
      padding: rem(1),
      borderRadius: rem(1),
      $nest: {
        '& h3': {
          margin: '1.5rem 0 1rem'
        },
        '& h3:first-child': {
          margin: '0 0 1rem 0'
        }
      }
    }),
  };
}
export default TrackVouchStatus;

export const ConnectedTrackVouchStatus = graphql(gql`
  query (
    $identity: ID!
  ) {
    getTrack(options:{identity:$identity}) {
      vouch_status
      youtube_content_owners {
        name
        active
      }
    }
  }
`, {
  options: (variables: Props) => ({
    variables,
    // fetchPolicy: 'network-only'
  }),
})(TrackVouchStatus);